import {
	BasicButton,
	BasicModal,
	InputProfileGroups,
	InputTextArea,
	SecondCustomInput,
	Typography,
} from "@/components";
import { BackAppBar } from "@/components/ui";
import { useBoolean } from "@/hooks/useBoolean";
import useCreateGroup from "@/hooks/useCreateGroup";
import { CreateGroupSchema, CreateGroupSearchParamSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import Button from "@mui/material/Button";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import isBoolean from "lodash/isBoolean";
import { useMemo } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/chat/group/create")({
	validateSearch: zodSearchValidator(CreateGroupSearchParamSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation("dashboard");
	const navigate = Route.useNavigate();
	const isDirectorySearchParam = Route.useSearch({
		select: (s) => s.directory,
	});
	const {
		value: isOpenSuccessCreated,
		setTrue: openSuccessCreated,
		setFalse: closeSuccessCreated,
	} = useBoolean();
	const { mutateAsync: mutateCreateGroup, data: resNewGroup } =
		useCreateGroup();
	const {
		control,
		register,
		watch,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<CreateGroupSchema>({
		mode: "all",
		resolver: zodResolver(CreateGroupSchema),
		defaultValues: {
			directory: isDirectorySearchParam,
		},
	});
	const { name: groupName, avatar } = watch();
	const profileUrl = useMemo(() => {
		if (!avatar) return undefined;
		if (avatar instanceof FileList) {
			if (!avatar.length) return undefined;
			// @ts-expect-error
			return URL.createObjectURL(avatar.item(0));
		}
		if (avatar instanceof File) return URL.createObjectURL(avatar);
		return avatar;
	}, [avatar]);
	const onSubmit: SubmitHandler<CreateGroupSchema> = async (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		return mutateCreateGroup(data, {
			onSuccess: () => {
				return openSuccessCreated();
			},
			onError: () => {
				toast.error(t("dashboard:group.createGroupFailed"));
			},
		});
	};
	const onClickAddPeople = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		if (!resNewGroup) return;
		return navigate({
			replace: true,
			to: "/dashboard/chat/group/$id/members",
			params: { id: resNewGroup.group.id },
		});
	};
	const onClickSkip = () => {
		if (!resNewGroup) return;
		closeSuccessCreated();
		return navigate({
			replace: true,
			to: "/dashboard/chat/group/$id/$channelName",
			params: {
				id: resNewGroup.group.id,
				channelName: resNewGroup.group.channel_name,
			},
		});
	};
	return (
		<>
			<BackAppBar to="/dashboard/chat/group" />
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="relative bg-white flex flex-col flex-1 overflow-hidden"
			>
				<div className="container flex flex-col flex-1 gap-8 pb-4 overflow-y-auto">
					<Typography fontSize={28} fontWeight={600}>
						{t("dashboard:group.createGroupTitle")}
					</Typography>
					<div className="flex flex-col justify-center items-center gap-6">
						<InputProfileGroups image={profileUrl} {...register("avatar")} />
						<Controller
							control={control}
							name="name"
							defaultValue=""
							render={({ field, fieldState }) => (
								<SecondCustomInput
									type="text"
									label={t("dashboard:group.groupName")}
									name={field.name}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>
						<Controller
							control={control}
							name="organization"
							defaultValue=""
							render={({ field, fieldState }) => (
								<SecondCustomInput
									type="text"
									label={t("dashboard:group.organization")}
									name={field.name}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>
						<Controller
							control={control}
							name="synopsis"
							defaultValue=""
							render={({ field, fieldState }) => (
								<InputTextArea
									fullWidth
									rows={4}
									maxLength={100}
									label={t("dashboard:group.groupSynopsis")}
									name={field.name}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									error={!!fieldState.error?.message}
								/>
							)}
						/>
						<Controller
							control={control}
							name="location"
							defaultValue=""
							render={({ field, fieldState }) => (
								<InputTextArea
									fullWidth
									rows={4}
									maxLength={100}
									label={t("dashboard:group.groupLocation")}
									name={field.name}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									error={!!fieldState.error?.message}
								/>
							)}
						/>
						<div className="w-full">
							<p className="text-dark text-sm font-bold py-2">
								{t("dashboard:group.addToDirectory")}
							</p>
							<Controller
								control={control}
								name="directory"
								render={({ field }) => (
									<>
										<div className="flex items-center my-2 gap-2">
											<BasicButton
												variant="outlined"
												color={field.value === true ? "info" : "inherit"}
												onClick={() => field.onChange(true)}
												onBlur={field.onBlur}
												className="w-full !h-12 !text-base font-medium !shadow-none"
												disable={field.disabled}
											>
												<div className="flex items-center gap-2">
													<CheckIcon />
													<p>{t("dashboard:group.yes")}</p>
												</div>
											</BasicButton>
											<BasicButton
												variant="outlined"
												color={field.value === false ? "error" : "inherit"}
												onClick={() => field.onChange(false)}
												onBlur={field.onBlur}
												className="w-full !h-12 !text-base font-medium !shadow-none"
												disable={field.disabled}
											>
												<div className="flex items-center gap-2">
													<CloseIcon />
													<p>{t("dashboard:group.no")}</p>
												</div>
											</BasicButton>
										</div>
										{isBoolean(field.value) ? (
											<div className="flex items-start gap-1">
												<InfoRoundedIcon className="!text-base" />
												<p className="text-dark text-xs font-medium">
													{field.value
														? t("dashboard:group.directoryYesHintGroup")
														: t("dashboard:group.directoryNoHintGroup")}
												</p>
											</div>
										) : null}
									</>
								)}
							/>
						</div>
					</div>
				</div>
				<div className="container py-4 shadow">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						className="w-full !h-12 text-sm font-semibold !shadow-none"
					>
						{t("dashboard:group.createGroup")}
					</BasicButton>
				</div>
			</form>
			<BasicModal isOpen={isOpenSuccessCreated} onClose={onClickSkip}>
				<div className="flex flex-col justify-center items-center gap-3">
					<Typography fontWeight={600} fontSize={16}>
						{t("dashboard:group.excellent")}
					</Typography>
					<Typography fontWeight={400} fontSize={14} align="center">
						{t("dashboard:group.createdSuccessMessage", { groupName })}
					</Typography>
					<Button
						size="large"
						variant="contained"
						color="primary"
						className="!rounded-full"
						fullWidth
						onClick={onClickAddPeople}
					>
						{t("dashboard:group.addPeopleToGroup")}
					</Button>
					<Button
						size="large"
						variant="outlined"
						color="primary"
						className="!rounded-full"
						onClick={onClickSkip}
						fullWidth
					>
						{t("dashboard:group.skip")}
					</Button>
				</div>
			</BasicModal>
		</>
	);
}
